import { OfferQuery } from '@1bill-app/services/offer/offer.query';
import { OfferService } from '@1bill-app/services/offer/offer.service';
import { OfferStore } from '@1bill-app/services/offer/offer.store';
import { Offer } from '@1bill-app/services/offer/offer.type';
import { RewardQuery } from '@1bill-app/services/reward/reward.query';
import { RewardService } from '@1bill-app/services/reward/reward.service';
import { UserQuery } from '@1bill-app/services/user/user.query';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Facebook } from '@awesome-cordova-plugins/facebook/ngx';
import { AlertController, LoadingController, ModalController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { SubSink } from 'subsink';
import { OfferDetailPageModalComponent } from './offer-detail/offer-detail.page';

/**
 * Page for displaying an offer
 */
@Component({
  selector: 'app-offer',
  templateUrl: './offer.page.html',
  styleUrls: ['./offer.page.scss'],
})
export class OfferPage implements OnInit, OnDestroy {
  offerDetails: Offer;

  totalPointsBalance$ = this.rewardQuery.select('totalPointsBalance');
  isLoading$ = this.offerQuery.isLoading$;
  offerId: number;
  categoryId: number;
  subs = new SubSink();
  title = 'Reward';

  get userHasFullAccess() {
    return this.userQuery.userHasFullAccess;
  }

  constructor(
    private route: ActivatedRoute,
    private logger: NGXLogger,
    private offerService: OfferService,
    private offerStore: OfferStore,
    private offerQuery: OfferQuery,
    private rewardQuery: RewardQuery,
    private rewardService: RewardService,
    private userQuery: UserQuery,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private modalCtrl: ModalController,
    private router: Router,
    private fb: Facebook,
  ) {}

  get isAccessingAsOtherUser() {
    return this.userQuery.isAccessingAsOtherUser;
  }

  ngOnInit() {
    this.fetchData();
    this.offerId = Number(this.route.snapshot.params.offerId);
    this.categoryId = Number(this.route.snapshot.params.catId);
    this.offerStore.update({ currOfferId: this.offerId, currCatId: this.categoryId });

    this.subs.sink = this.offerQuery.offerDetails$.subscribe((res) => {
      this.offerDetails = res;
      this.title = this.offerDetails.title;
    });
    // testing the FB SDK event tracking
    this.fb.logEvent('View content', {
      contentID: this.offerId,
      contentType: this.categoryId,
    });
  }

  fetchData() {
    this.subs.sink = this.offerService.getOffers().subscribe();
    this.subs.sink = this.rewardService.getRewards(true).subscribe();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  async redeemOffer(offer: Offer) {
    if (!this.userHasFullAccess) {
      this.alertController.create({
        header: 'Not authorised to redeem',
        message:
          'The owner of this account has not granted you full access which you need to redeem an offer.',
        buttons: ['OK'],
      });
    }
    const loading = await this.loadingController.create({
      message: 'Redeeming Offer...',
    });
    await loading.present();
    try {
      this.logger.log('Offer: ', offer);
      // if (offer?.redeemed === 1) {
      //   await loading.dismiss();
      //   this.logger.log('Offer already redeemed');
      //   this.presentAlertPopover(
      //     'Offer has already been redeemed',
      //     'You have already redeemed this offer.',
      //   );
      //   return;
      // }
      this.logger.log('Redeem offer');
      const userPoints = this.rewardQuery.getValue().totalPointsBalance;
      this.logger.log('User points: ', userPoints);
      const offerPoint1 = offer?.point_1;
      if (userPoints >= offerPoint1) {
        // Can redeem
        this.logger.log('Redeeming offer...');
        const redeemResult = await this.offerService.redeemOffer(offer?.id).toPromise();
        this.logger.debug('Redeem result: ', redeemResult);

        if (!redeemResult.success) {
          this.presentAlertPopover('Oops!', 'Something went wrong!');
        } else if (redeemResult.success && redeemResult.code === 'UNDER_ADMIN_APPROVAL') {
          this.logger.log(redeemResult.code);
          this.presentRewardRedeemWaitAlertPopover('Great news!', redeemResult.message);
        } else {
          this.presentOfferDetailIframe(redeemResult?.data?.url);
        }
        // refresh offers and rewards
        this.fetchData();
        // this.presentSuccessfulRedeem(redeemResult?.redeemId);
      } else {
        this.logger.log('No enough points');
        this.presentAlertPopover(
          'Not enough points',
          'You do not have enough points to redeem this offer.',
        );
      }
    } catch (e) {
      this.logger.error('Error redeeming offer: ', e);
      this.presentAlertPopover('Oops!', e?.error?.message ?? 'Internal server error');
    } finally {
      loading.dismiss();
    }
  }

  async presentOfferDetailIframe(url: string) {
    const modal = await this.modalCtrl.create({
      component: OfferDetailPageModalComponent,
      cssClass: 'card-modal',
      swipeToClose: true,
      mode: 'ios',
      componentProps: { url },
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  async presentSuccessfulRedeem(redeemId?: number) {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message:
        '<img src="assets/icon/alert/success.svg" alt="warning-icon" /><h2 class="alert-title">Offer has been redeemed</h2><p class="alert-text">Your offer has been redeemed.</p>',
      buttons: [
        {
          text: 'Okay',
          handler: () => {
            if (redeemId) this.router.navigate(['/app/offers/redeemed/' + redeemId]);
          },
        },
      ],
    });

    await alert.present();
  }

  async presentAlertPopover(title: string, message: string) {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message: `<img src="assets/icon/alert/warning.svg" alt="warning-icon" /><h2 class="alert-title">${title}</h2><p class="alert-text">${message}</p>`,
      buttons: [
        {
          text: 'Okay',
        },
      ],
    });

    await alert.present();
  }
  async presentRewardRedeemWaitAlertPopover(title: string, message: string) {
    const alert = await this.alertController.create({
      mode: 'ios',
      cssClass: 'bill-alert alert-icon',
      message: `<img src="assets/icon/alert/successTwo.svg" alt="success-icon" /><h2 class="alert-title">${title}</h2><p class="alert-text">${message}</p>`,
      buttons: [
        {
          text: 'Okay',
        },
      ],
    });

    await alert.present();
  }
}
